import { DlpInfo, FileResponseInterface, ScanResults } from 'services/interface';
import { getMultiscanVerdict, getScanVerdict } from 'utils/verdict/Verdict';

// The predictive engine key in scan_details may arrive under either name.
export const PREDICTIVE_ENGINE_NAMES = ['OPSWAT AI-Predictive Malware Detection', 'OPSWAT Predictive Alin AI'];

// True only when the Predictive AI engine itself flagged the file — not when other AV engines did.
// Shared by the Overview card and the Threats Detected header badge so the two never disagree.
export const isPredictiveAiDetected = (fileData?: FileResponseInterface): boolean => {
    const scanResults = fileData?.scan_results;
    const detail = PREDICTIVE_ENGINE_NAMES.map((name) => scanResults?.scan_details?.[name]).find(Boolean);
    // scan_result_i 23 = engine ran but abstained ("Not Supported"); a missing detail = engine unavailable.
    if (!detail || detail.scan_result_i === 23) {
        return false;
    }
    return getScanVerdict(detail.scan_result_i)?.variant === 'alert';
};

// The platform's `total_detected_avs` only counts Infected/Suspicious engines: an engine returning
// 22 (Potentially Unwanted Program) leaves it at 0 even though the engine did flag the file, so the
// Overview card and the Metascan header claimed "No Threats Detected" while the Threats table drew
// red rows for those engines (MDCL-18093). Derive the count from scan_details with the same
// getScanVerdict mapping the table uses, so the count and the rows agree by construction, and never
// go below the backend's number.
export const countDetectedEngines = (scanResults?: ScanResults): number => {
    const details = scanResults?.scan_details;
    const fromDetails = details
        ? Object.values(details).filter((detail) => getScanVerdict(detail?.scan_result_i)?.label === 'Detected').length
        : 0;
    return Math.max(fromDetails, scanResults?.total_detected_avs ?? 0);
};

// MetaScan only ran when at least one AV engine reported: `total_avs === 0` is the platform's way of
// saying nothing scanned the file — an unsupported type, or a workflow that never included MetaScan
// (`current_av_result_a: "Not Scanned"`). Shared by the Overview card and the Download menu's
// Prevention Report entry so the two can never disagree: that report is generated from
// `scan_results`, so an unscanned file gives it nothing to render.
export const hasMetascanRun = (scanResults?: ScanResults): boolean => (scanResults?.total_avs ?? 0) > 0;

// A clean headline needs an analysis behind it. Every technology has a way of not finishing — engines
// that aborted, failed or refused the type, a sandbox that gave up, a sanitization that errored — and
// when none of them finished, "No Threats Detected" is fail-open: nothing ever looked at the bytes
// (MDCI-976). Each gate mirrors the matching Overview card's active / failed check, so the header only
// claims a verdict a card below can back up. `sandboxVerdict` is whichever verdict string the caller
// resolved (filescan or report); Unknown is the sandbox declining to answer, not an answer.
export const hasCompletedAnalysis = (fileData?: FileResponseInterface, sandboxVerdict?: string): boolean => {
    const scanResults = fileData?.scan_results;
    const metascanFinished = hasMetascanRun(scanResults) && getMultiscanVerdict(scanResults).label !== 'Failed';
    const sandboxAnswered = Boolean(sandboxVerdict) && sandboxVerdict?.toLowerCase() !== 'unknown';
    const cdrRan = Boolean(fileData?.process_info?.post_processing?.sanitization_details || fileData?.sanitized);
    const cdrFinished = cdrRan && fileData?.sanitized?.result !== 'Error';
    // DLP verdict codes 3 (Failed) and 4 (Not Supported) — see verdictDLP.
    const dlpCode = fileData?.dlp_info?.verdict;
    const dlpFinished = Boolean(fileData?.dlp_info) && dlpCode !== 3 && dlpCode !== 4;
    const aigcdFinished = Boolean(fileData?.aigcd_info?.final_verdict?.verdict);
    return metascanFinished || sandboxAnswered || cdrFinished || dlpFinished || aigcdFinished;
};

export const sumSanitizedObjectCounts = (fileData?: FileResponseInterface): number => {
    const rows = fileData?.process_info?.post_processing?.sanitization_details?.details;
    if (!rows || rows.length === 0) {
        return 0;
    }

    let total = 0;
    for (const row of rows) {
        const count = typeof row.count === 'number' ? row.count : Number.parseInt(String(row.count), 10);
        total += Number.isNaN(count) ? 0 : count;
    }
    return total;
};

export const sumDlpHitRows = (dlp?: DlpInfo): number => {
    if (!dlp?.hits) {
        return 0;
    }

    let total = 0;
    const categories = Object.values(dlp.hits);
    for (const category of categories) {
        if (category?.hits?.length !== undefined) {
            total += category.hits.length;
        }
    }
    return total;
};

/**
 * An Aether scan (MDCI-919). The workflow name is on the first response (`process_info.profile`), the
 * engine blocks (`reputation_info`, `threatintelligence_info`) land as each stage completes — either
 * one says Aether, so the cards and the tab show from the first frame instead of popping in later.
 */
export const isAetherResult = (
    fileData?: Partial<Pick<FileResponseInterface, 'process_info' | 'reputation_info' | 'threatintelligence_info'>>,
): boolean =>
    Boolean(
        fileData?.process_info?.profile?.toLowerCase().startsWith('aether') ||
            fileData?.reputation_info ||
            fileData?.threatintelligence_info,
    );
